/**
 * Which player versions support which CMS feature. Centralized so every surface
 * agrees on the threshold — no version literals scattered through components.
 * Mirrors ./capabilities, which does the same for device-kind gating. Gates the
 * server resolves itself live in lib/player_version.rb instead.
 *
 * Player versions look like `major.minor.patch-build` (e.g. "1.19.0-2194") and
 * compare segment by segment, so the build only breaks ties within the same
 * `major.minor.patch` line.
 */

type MaybeVersion = string | null | undefined;

/** Minimum player version required for each version-gated feature. */
export const playerFeatureMinimums = {
  /** Configurable status report interval — earlier players hardcode 30s. */
  statusInterval: '1.18.5-1676',
  /** Non-PubNub messaging backends (MQTT, AWS IoT). */
  messagingBackend: '1.19.0-2112',
  /** Remote control button presses (up/down/left/right/ok/back/home/menu). */
  remoteControl: '1.19.0-2194',
  /** Preloading the next content item while the current one plays. */
  preload: '1.19.0-2222',
} as const;

export type PlayerFeature = keyof typeof playerFeatureMinimums;

/**
 * Parses a player version like "1.19.0-2194" into its numeric segments.
 * Returns null if the version is missing or isn't in that shape.
 */
const parsePlayerVersion = (version: MaybeVersion) => {
  const match = version?.match(/^(\d+)\.(\d+)\.(\d+)(?:-(\d+))?$/);
  if (!match) return null;

  return {
    major: parseInt(match[1]),
    minor: parseInt(match[2]),
    patch: parseInt(match[3]),
    build: match[4] ? parseInt(match[4]) : 0,
  };
};

/** Returns true if the version is a development build, e.g. "1.19.0-dev". */
const isDevPlayerVersion = (version: MaybeVersion) => /^\d+\.\d+\.\d+-dev$/.test(version ?? '');

/**
 * Returns true if `version` is at least `minimum`, comparing major, then minor,
 * then patch, then build. Dev builds always qualify so they get the latest
 * features. Unknown or unparseable versions never do, so a device that hasn't
 * reported a version is gated off.
 */
export const playerVersionAtLeast = (version: MaybeVersion, minimum: MaybeVersion) => {
  if (isDevPlayerVersion(version)) return true;

  const parsed = parsePlayerVersion(version);
  const parsedMinimum = parsePlayerVersion(minimum);

  if (!parsed || !parsedMinimum) return false;

  if (parsed.major !== parsedMinimum.major) return parsed.major > parsedMinimum.major;
  if (parsed.minor !== parsedMinimum.minor) return parsed.minor > parsedMinimum.minor;
  if (parsed.patch !== parsedMinimum.patch) return parsed.patch > parsedMinimum.patch;

  return parsed.build >= parsedMinimum.build;
};

/** Returns true if the player version supports the given feature. */
export const playerSupports = (version: MaybeVersion, feature: PlayerFeature) =>
  playerVersionAtLeast(version, playerFeatureMinimums[feature]);
